import { lazy, Suspense }          from 'react'
import { Routes, Route, Navigate, Outlet, useParams } from 'react-router-dom'

import { ROUTES, homePathForRole } from '@constants/routes'
import { useAuthStore }            from '@store/authStore'
import { AdminRoute }              from './AdminRoute'
import { CompanyAdminOnlyRoute }   from './CompanyAdminOnlyRoute'
import { PortalRoute }             from './PortalRoute'
import { PublicRoute }             from './PublicRoute'
import { PermissionRoute }         from './PermissionRoute'
import { MainLayout }              from '@layouts/MainLayout'
import { AuthLayout }              from '@layouts/AuthLayout'
import { SettingsLayout }          from '@layouts/SettingsLayout'
import { ErrorBoundary, PageLoader } from '@components/feedback'

// ── Auth pages ────────────────────────────────────────────────────────────────
const LoginPage          = lazy(() => import('@pages/auth/LoginPage'))
const ForgotPasswordPage = lazy(() => import('@pages/auth/ForgotPasswordPage'))
const ResetPasswordPage  = lazy(() => import('@pages/auth/ResetPasswordPage'))

// ── Admin pages ───────────────────────────────────────────────────────────────
const DashboardPage      = lazy(() => import('@pages/dashboard/DashboardPage'))

const EventListPage      = lazy(() => import('@pages/events/EventListPage'))
const EventCreatePage    = lazy(() => import('@pages/events/EventCreatePage'))
const EventDetailPage    = lazy(() => import('@pages/events/EventDetailPage'))
const EventEditPage      = lazy(() => import('@pages/events/EventEditPage'))

const UserListPage       = lazy(() => import('@pages/admin/users/UserListPage'))
const UserCreatePage     = lazy(() => import('@pages/admin/users/UserCreatePage'))
const UserDetailPage     = lazy(() => import('@pages/admin/users/UserDetailPage'))
const UserEditPage       = lazy(() => import('@pages/admin/users/UserEditPage'))

const ReportsOverviewPage      = lazy(() => import('@pages/reports/ReportsOverviewPage'))
const EventReportsPage         = lazy(() => import('@pages/reports/EventReportsPage'))
const UserReportsPage          = lazy(() => import('@pages/reports/UserReportsPage'))
const RegistrationReportsPage  = lazy(() => import('@pages/reports/RegistrationReportsPage'))

const ProfileSettingsPage      = lazy(() => import('@pages/settings/ProfileSettingsPage'))
const AccountSettingsPage      = lazy(() => import('@pages/settings/AccountSettingsPage'))
const OrganizationSettingsPage = lazy(() => import('@pages/settings/OrganizationSettingsPage'))
const BrandingSettingsPage     = lazy(() => import('@pages/settings/BrandingSettingsPage'))
const RegionalSettingsPage     = lazy(() => import('@pages/settings/RegionalSettingsPage'))
const NotificationSettingsPage = lazy(() => import('@pages/settings/NotificationSettingsPage'))
const SecuritySettingsPage     = lazy(() => import('@pages/settings/SecuritySettingsPage'))

// ── Portal pages ──────────────────────────────────────────────────────────────
const PortalDashboardPage        = lazy(() => import('@pages/portal/PortalDashboardPage'))
const PortalEventDetailPage      = lazy(() => import('@pages/portal/PortalEventDetailPage'))
const MyEventsPage               = lazy(() => import('@pages/portal/MyEventsPage'))
const PortalProfilePage          = lazy(() => import('@pages/portal/PortalProfilePage'))
const PortalSettingsPage         = lazy(() => import('@pages/portal/PortalSettingsPage'))
const PortalNotificationsPage    = lazy(() => import('@pages/portal/PortalNotificationsPage'))
const NotificationPreferencesPage = lazy(() => import('@pages/portal/NotificationPreferencesPage'))

// ── Admin connections pages ───────────────────────────────────────────────────
const AdminConnectionDetailPage  = lazy(() => import('@pages/admin/connections/AdminConnectionDetailPage'))

// ── Portal connections pages ──────────────────────────────────────────────────
const PortalCreateConnectionPage = lazy(() => import('@pages/portal/connections/CreateConnectionPage'))
const PortalConnectionDetailPage = lazy(() => import('@pages/portal/connections/ConnectionDetailPage'))
const PortalEditConnectionPage   = lazy(() => import('@pages/portal/connections/EditConnectionPage'))

// ── Admin notification pages ───────────────────────────────────────────────────
const AdminNotificationsPage     = lazy(() => import('@pages/admin/notifications/AdminNotificationsPage'))
const NotificationHistoryPage    = lazy(() => import('@pages/admin/notifications/NotificationHistoryPage'))

// ── Communication pages (Email Templates now live here) ────────────────────────
const CommEmailTemplatesPage  = lazy(() => import('@pages/admin/communication/EmailTemplatesPage'))
const CommEmailTemplateEditor = lazy(() => import('@pages/admin/communication/EmailTemplateEditorPage'))

// ── Outreach / Sent Emails ────────────────────────────────────────────────────
const SentEmailsPage          = lazy(() => import('@pages/admin/emails/SentEmailsPage'))

const CompanyListPage         = lazy(() => import('@pages/admin/companies/CompanyListPage'))
const CompanyCreatePage       = lazy(() => import('@pages/admin/companies/CompanyCreatePage'))
const CompanyDetailPage       = lazy(() => import('@pages/admin/companies/CompanyDetailPage'))
const CompanyEditPage         = lazy(() => import('@pages/admin/companies/CompanyEditPage'))


const NotFoundPage          = lazy(() => import('@pages/NotFoundPage'))
const ForbiddenPage         = lazy(() => import('@pages/errors/ForbiddenPage'))

// ── Role pages ────────────────────────────────────────────────────────────────
const RoleListPage          = lazy(() => import('@pages/admin/roles/RoleListPage'))
const RoleCreatePage        = lazy(() => import('@pages/admin/roles/RoleCreatePage'))
const RoleDetailPage        = lazy(() => import('@pages/admin/roles/RoleDetailPage'))
const RoleEditPage          = lazy(() => import('@pages/admin/roles/RoleEditPage'))

// ── Legacy redirect helpers ───────────────────────────────────────────────────
// Preserves :id when redirecting old notification template URLs to new paths.
function LegacyTemplateRedirect() {
  const { id } = useParams()
  return <Navigate to={ROUTES.ADMIN.COMMUNICATION.TEMPLATE_DETAIL(id)} replace />
}

// ── Root redirect ─────────────────────────────────────────────────────────────
// Sends "/" to the role-appropriate home, or to login when signed out.
function RootRedirect() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
  const user            = useAuthStore((s) => s.user)
  if (!isAuthenticated) return <Navigate to={ROUTES.AUTH.LOGIN} replace />
  return <Navigate to={homePathForRole(user?.role)} replace />
}

// ── Section boundary ─────────────────────────────────────────────────────────
// Sits between MainLayout and page-level routes. Any page crash is caught here
// while the sidebar and header (inside MainLayout) keep rendering normally.
function RouteBoundary() {
  return (
    <ErrorBoundary>
      <Outlet />
    </ErrorBoundary>
  )
}

// ── Router ──────────────────────────────────────────────────────────────────────
export function AppRouter() {
  return (
    // Top-level boundary: catches any error that escapes the section boundaries
    // (e.g. a crash inside a route guard or layout itself).
    <ErrorBoundary>
    <Suspense fallback={<PageLoader />}>
      <Routes>

        {/* ── Root ──────────────────────────────────────────────────── */}
        <Route index element={<RootRedirect />} />

        {/* ── Auth (public only) ────────────────────────────────────── */}
        <Route element={<PublicRoute />}>
          <Route element={<AuthLayout />}>
            <Route path={ROUTES.AUTH.LOGIN}    element={<LoginPage />} />
            <Route path={ROUTES.AUTH.REGISTER} element={<Navigate to={ROUTES.AUTH.LOGIN} replace />} />
            <Route path={ROUTES.AUTH.FORGOT}   element={<ForgotPasswordPage />} />
            <Route path={ROUTES.AUTH.RESET}    element={<ResetPasswordPage />} />
          </Route>
        </Route>

        {/* ── Admin console (role === 'admin') ──────────────────────── */}
        <Route element={<AdminRoute />}>
          <Route element={<MainLayout />}>
            {/* RouteBoundary: page crashes are caught here; sidebar + header survive */}
            <Route element={<RouteBoundary />}>
            <Route path={ROUTES.ADMIN.DASHBOARD} element={<DashboardPage />} />

            {/* Companies (Super Admin) */}
            <Route element={<PermissionRoute permission="companies:read" />}>
              <Route path={ROUTES.ADMIN.COMPANIES.LIST} element={<CompanyListPage />} />
              <Route path={ROUTES.ADMIN.COMPANIES.DETAIL()} element={<CompanyDetailPage />} />
            </Route>
            <Route element={<PermissionRoute permission="companies:create" />}>
              <Route path={ROUTES.ADMIN.COMPANIES.CREATE} element={<CompanyCreatePage />} />
            </Route>
            <Route element={<PermissionRoute permission="companies:update" />}>
              <Route path={ROUTES.ADMIN.COMPANIES.EDIT()} element={<CompanyEditPage />} />
            </Route>

            {/* Company-admin operational routes — hidden from super_admin */}
            <Route element={<CompanyAdminOnlyRoute />}>
            {/* Events */}
            <Route path={ROUTES.ADMIN.EVENTS.LIST}          element={<EventListPage />} />
            <Route path={ROUTES.ADMIN.EVENTS.CREATE}        element={<EventCreatePage />} />
            <Route path={ROUTES.ADMIN.EVENTS.DETAIL()}      element={<EventDetailPage />} />
            <Route path={ROUTES.ADMIN.EVENTS.EDIT()}        element={<EventEditPage />} />

            {/* Users */}
            <Route path={ROUTES.ADMIN.USERS.LIST}           element={<UserListPage />} />
            <Route path={ROUTES.ADMIN.USERS.CREATE}         element={<UserCreatePage />} />
            <Route path={ROUTES.ADMIN.USERS.DETAIL()}       element={<UserDetailPage />} />
            <Route path={ROUTES.ADMIN.USERS.EDIT()}         element={<UserEditPage />} />

            {/* Assignments — moved into event create/edit; legacy redirects */}
            <Route path={ROUTES.ADMIN.ASSIGNMENTS.LIST}     element={<Navigate to={ROUTES.ADMIN.EVENTS.LIST} replace />} />
            <Route path={ROUTES.ADMIN.ASSIGNMENTS.DETAIL()} element={<Navigate to={ROUTES.ADMIN.EVENTS.LIST} replace />} />
            <Route path={ROUTES.ADMIN.REGISTRATIONS.LIST}   element={<Navigate to={ROUTES.ADMIN.EVENTS.LIST} replace />} />
            <Route path={ROUTES.ADMIN.REGISTRATIONS.DETAIL()} element={<Navigate to={ROUTES.ADMIN.EVENTS.LIST} replace />} />

            {/* Reports */}
            <Route path={ROUTES.ADMIN.REPORTS.OVERVIEW}     element={<ReportsOverviewPage />} />
            <Route path={ROUTES.ADMIN.REPORTS.EVENTS}       element={<EventReportsPage />} />
            <Route path={ROUTES.ADMIN.REPORTS.USERS}        element={<UserReportsPage />} />
            <Route path={ROUTES.ADMIN.REPORTS.ASSIGNMENTS}  element={<RegistrationReportsPage />} />

            {/* Notifications */}
            <Route path={ROUTES.ADMIN.NOTIFICATIONS.LIST}    element={<AdminNotificationsPage />} />
            <Route path={ROUTES.ADMIN.NOTIFICATIONS.HISTORY} element={<NotificationHistoryPage />} />
            {/* Legacy redirects — old template URLs → new Communication paths */}
            <Route path={ROUTES.ADMIN.NOTIFICATIONS.TEMPLATES}         element={<Navigate to={ROUTES.ADMIN.COMMUNICATION.TEMPLATES} replace />} />
            <Route path={ROUTES.ADMIN.NOTIFICATIONS.TEMPLATE_DETAIL()} element={<LegacyTemplateRedirect />} />

            {/* Communication — Email Templates */}
            <Route path={ROUTES.ADMIN.COMMUNICATION.TEMPLATES}       element={<CommEmailTemplatesPage />} />
            <Route path={ROUTES.ADMIN.COMMUNICATION.TEMPLATE_DETAIL()} element={<CommEmailTemplateEditor />} />

            {/* Outreach — Sent Emails */}
            <Route path={ROUTES.ADMIN.EMAILS.SENT} element={<SentEmailsPage />} />

            {/* Connections — managed per event; legacy list/analytics redirect */}
            <Route path={ROUTES.ADMIN.CONNECTIONS.LIST}      element={<Navigate to={ROUTES.ADMIN.EVENTS.LIST} replace />} />
            <Route path={ROUTES.ADMIN.CONNECTIONS.ANALYTICS} element={<Navigate to={ROUTES.ADMIN.EVENTS.LIST} replace />} />
            <Route path={ROUTES.ADMIN.CONNECTIONS.DETAIL()}  element={<AdminConnectionDetailPage />} />

            {/* Roles & Permissions */}
            <Route element={<PermissionRoute permission="roles:read" />}>
              <Route path={ROUTES.ROLES.LIST}   element={<RoleListPage />} />
              <Route path={ROUTES.ROLES.DETAIL()} element={<RoleDetailPage />} />
            </Route>
            <Route element={<PermissionRoute permission="roles:create" />}>
              <Route path={ROUTES.ROLES.CREATE} element={<RoleCreatePage />} />
            </Route>
            <Route element={<PermissionRoute permission="roles:update" />}>
              <Route path={ROUTES.ROLES.EDIT()} element={<RoleEditPage />} />
            </Route>
            </Route>{/* end CompanyAdminOnlyRoute */}

            {/* Settings — available to all admin roles */}
            <Route element={<SettingsLayout />}>
              <Route
                index
                path={ROUTES.ADMIN.SETTINGS.ROOT}
                element={<Navigate to={ROUTES.ADMIN.SETTINGS.PROFILE} replace />}
              />
              <Route path={ROUTES.ADMIN.SETTINGS.PROFILE}       element={<ProfileSettingsPage />} />
              <Route path={ROUTES.ADMIN.SETTINGS.ACCOUNT}       element={<AccountSettingsPage />} />
              <Route path={ROUTES.ADMIN.SETTINGS.ORGANIZATION}  element={<OrganizationSettingsPage />} />
              <Route path={ROUTES.ADMIN.SETTINGS.BRANDING}      element={<BrandingSettingsPage />} />
              <Route path={ROUTES.ADMIN.SETTINGS.REGIONAL}      element={<RegionalSettingsPage />} />
              <Route path={ROUTES.ADMIN.SETTINGS.NOTIFICATIONS} element={<NotificationSettingsPage />} />
              <Route path={ROUTES.ADMIN.SETTINGS.SECURITY}      element={<SecuritySettingsPage />} />
            </Route>

            </Route>{/* end RouteBoundary */}
          </Route>
        </Route>

        {/* ── User portal (role === 'user' OR 'admin') ──────────────── */}
        <Route element={<PortalRoute />}>
          <Route element={<MainLayout />}>
            {/* RouteBoundary: page crashes are caught here; sidebar + header survive */}
            <Route element={<RouteBoundary />}>
            <Route path={ROUTES.PORTAL.DASHBOARD}       element={<PortalDashboardPage />} />
            <Route path={ROUTES.PORTAL.EVENT_DETAIL()}  element={<PortalEventDetailPage />} />
            <Route path={ROUTES.PORTAL.MY_EVENTS}       element={<MyEventsPage />} />
            <Route path={ROUTES.PORTAL.PROFILE}       element={<PortalProfilePage />} />
            <Route path={ROUTES.PORTAL.SETTINGS}      element={<PortalSettingsPage />} />
            <Route path={ROUTES.PORTAL.NOTIFICATIONS} element={<PortalNotificationsPage />} />
            <Route path={ROUTES.PORTAL.SETTINGS_PAGES.NOTIFICATIONS} element={<NotificationPreferencesPage />} />

            {/* Portal Connections — create/detail/edit only; list moved under events */}
            <Route path={ROUTES.PORTAL.CONNECTIONS.LIST} element={<Navigate to={ROUTES.PORTAL.MY_EVENTS} replace />} />
            <Route path={ROUTES.PORTAL.CONNECTIONS.CREATE} element={<PortalCreateConnectionPage />} />
            <Route path={ROUTES.PORTAL.CONNECTIONS.DETAIL()} element={<PortalConnectionDetailPage />} />
            <Route path={ROUTES.PORTAL.CONNECTIONS.EDIT()}  element={<PortalEditConnectionPage />} />
            </Route>{/* end RouteBoundary */}
          </Route>
        </Route>

        {/* ── Legacy redirects (bookmark safety; safe to remove later) ─ */}
        <Route path="/dashboard"          element={<Navigate to={ROUTES.ADMIN.DASHBOARD} replace />} />
        <Route path="/events"             element={<Navigate to={ROUTES.ADMIN.EVENTS.LIST} replace />} />
        <Route path="/events/create"      element={<Navigate to={ROUTES.ADMIN.EVENTS.CREATE} replace />} />
        <Route path="/users"              element={<Navigate to={ROUTES.ADMIN.USERS.LIST} replace />} />
        <Route path="/registrations"      element={<Navigate to={ROUTES.ADMIN.EVENTS.LIST} replace />} />
        <Route path="/reports/overview"   element={<Navigate to={ROUTES.ADMIN.REPORTS.OVERVIEW} replace />} />
        <Route path="/reports/events"     element={<Navigate to={ROUTES.ADMIN.REPORTS.EVENTS} replace />} />
        <Route path="/settings/profile"   element={<Navigate to={ROUTES.ADMIN.SETTINGS.PROFILE} replace />} />
        <Route path="/settings/account"   element={<Navigate to={ROUTES.ADMIN.SETTINGS.ACCOUNT} replace />} />

        {/* ── 403 ───────────────────────────────────────────────────── */}
        <Route path={ROUTES.FORBIDDEN} element={<ForbiddenPage />} />

        {/* ── 404 ───────────────────────────────────────────────────── */}
        <Route path={ROUTES.NOT_FOUND} element={<NotFoundPage />} />

      </Routes>
    </Suspense>
    </ErrorBoundary>
  )
}
